import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import { logger } from "@/lib/logger";
import { useQuickBooks } from "@/lib/contexts/QuickBooksContext";
import type { QuickBooksInvoice, QuickBooksCustomer, CompanyMetadata } from "@/lib/types/quickbooks-unified";
import type { MultiCompanyItem, MultiCompanyResult } from "@/lib/integrations/quickbooks/quickbooks-multi-company-utils";
import {
  parseQuickBooksApiError,
  parseQuickBooksEntityListResponse,
  parseQuickBooksPerRealmListResponse,
} from "@/lib/types/quickbooks/api-validation";

export function useQuickBooksEntitiesMultiCompany(
  entityType: "customers" | "invoices" = "customers"
) {
  const { session } = useUniversalAuth();
  const { connections } = useQuickBooks();

  const realmIds = React.useMemo(() => {
    return Object.keys(connections || {});
  }, [connections]);

  return useQuery({
    queryKey: [`quickbooks-${entityType}-multi-company`, realmIds],
    queryFn: async (): Promise<MultiCompanyResult<QuickBooksInvoice | QuickBooksCustomer>> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      if (realmIds.length === 0) {
        return { items: [], totalCount: 0, companiesCount: 0, companies: [] };
      }

      type PerRealmResult = {
        items?: Array<QuickBooksInvoice | QuickBooksCustomer>;
        companies?: CompanyMetadata[];
        totalCount?: number;
        companiesCount?: number;
        realmId: string;
      };

      const promises = realmIds.map(async (realmId): Promise<PerRealmResult> => {
        const response = await fetch(
          `/api/quickbooks/entities?all=true&realmId=${realmId}&entityType=${entityType}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );

        if (!response.ok) {
          logger.warn(
            `Failed to load QuickBooks ${entityType} for realm ${realmId}`,
            { status: response.status }
          );
          return { items: [], totalCount: 0, companiesCount: 0, companies: [], realmId };
        }
        const data: unknown = await response.json();
        const parsed = parseQuickBooksPerRealmListResponse(data);
        return { ...parsed, realmId };
      });

      const results = await Promise.all(promises);

      const totalCount = results.reduce((sum, result) => sum + (result.totalCount ?? 0), 0);
      const companiesByRealm = results.reduce(
        (acc, result) => {
          if (Array.isArray(result.companies)) {
            for (const company of result.companies) {
              acc.set(company.realmId, company);
            }
          }
          return acc;
        },
        new Map<string, CompanyMetadata>()
      );

      const allItems: MultiCompanyItem<QuickBooksCustomer | QuickBooksInvoice>[] =
        results.flatMap((result) => {
          const company = companiesByRealm.get(result.realmId);
          if (!company) return [];
          return (result.items ?? []).map((data) => ({ data, company }));
        });

      return {
        items: allItems,
        totalCount,
        companiesCount: realmIds.length,
        companies: Array.from(companiesByRealm.values()),
      };
    },
    enabled: !!session?.access_token && realmIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}

export function useQuickBooksEntitiesByCustomerMultiCompany(
  entityType: "invoices" = "invoices",
  customerName?: string,
  customerId?: string,
  realmId?: string
) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: [
      `quickbooks-${entityType}-multi-company`,
      "customer",
      customerName || null,
      customerId || null,
      realmId || null,
    ],
    // `entityType` is pinned to "invoices" in this hook's signature, so the
    // route really does return invoices.
    queryFn: async (): Promise<MultiCompanyResult<QuickBooksInvoice>> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      if (!customerId && !(customerName && customerName.trim())) {
        throw new Error("Customer reference (ID) or name is required");
      }

      const queryParams = new URLSearchParams({
        entityType: entityType,
        ...(customerId && { customerId }),
        ...(customerName && !customerId && { search: customerName.trim() }),
        ...(realmId && { realmId }),
      });

      const response = await fetch(`/api/quickbooks/entities?${queryParams.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorBody: unknown = await response.json().catch(() => ({}));
        const error = parseQuickBooksApiError(errorBody);
        throw new Error(error.error || "Failed to fetch customer entities");
      }

      const data: unknown = await response.json();
      return parseQuickBooksEntityListResponse<QuickBooksInvoice>(data);
    },
    enabled: !!session?.access_token && (!!customerId || !!(customerName && customerName.trim())),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useQuickBooksInvoicesByMultipleCustomersMultiCompany(
  customerRefs: Array<{ customerId: string; realmId?: string }>
) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: [
      "quickbooks-invoices-multi-company",
      "multiple-customers",
      customerRefs
        .map((ref) => `${ref.customerId}:${ref.realmId || ""}`)
        .sort()
        .join(","),
    ],
    queryFn: async (): Promise<MultiCompanyResult<QuickBooksInvoice>> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      if (!customerRefs || customerRefs.length === 0) {
        throw new Error("At least one customer reference is required");
      }

      type PerCustomerResult = {
        items?: QuickBooksInvoice[];
        companies?: CompanyMetadata[];
        totalCount?: number;
        companiesCount?: number;
        realmId?: string;
      };

      const promises = customerRefs.map(async ({ customerId, realmId }): Promise<PerCustomerResult> => {
        const queryParams = new URLSearchParams({
          entityType: "invoices",
          customerId: customerId,
          ...(realmId && { realmId }),
        });

        const response = await fetch(`/api/quickbooks/entities?${queryParams.toString()}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          const errorBody: unknown = await response.json().catch(() => ({}));
          const error = parseQuickBooksApiError(errorBody);
          throw new Error(error.error || `Failed to fetch invoices for customer ${customerId}`);
        }

        const data: unknown = await response.json();
        // `entityType: "invoices"` above — tell the parser what was requested
        // instead of narrowing the union afterwards.
        const parsed = parseQuickBooksPerRealmListResponse<QuickBooksInvoice>(data);
        return { ...parsed, realmId, items: parsed.items };
      });

      const results = await Promise.all(promises);

      const companiesByRealm = new Map<string, CompanyMetadata>();
      let totalCount = 0;
      let companiesCount = 0;

      results.forEach((result) => {
        if (Array.isArray(result.companies)) {
          for (const company of result.companies) {
            companiesByRealm.set(company.realmId, company);
          }
        }
        totalCount += result.totalCount ?? 0;
        companiesCount = Math.max(companiesCount, result.companiesCount ?? 0);
      });

      const allItems: MultiCompanyItem<QuickBooksInvoice>[] = results.flatMap((result) => {
        const company =
          (result.realmId ? companiesByRealm.get(result.realmId) : undefined) ??
          result.companies?.[0];
        if (!company) return [];
        return (result.items ?? []).map((data) => ({ data, company }));
      });

      return {
        items: allItems,
        companies: Array.from(companiesByRealm.values()),
        totalCount,
        companiesCount,
      };
    },
    enabled: !!session?.access_token && customerRefs.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
